import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  IconButton,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Controller } from "react-hook-form";

import PatientAutocomplete from "../../../components/Advanced/Autocomplete/PatientAutocomplete";
import {
  TASK_CATEGORIES,
  TASK_PRIORITIES,
  RELATED_TYPES,
} from "../constants/taskConstants";

/**
 * Dialog para crear o editar tareas.
 *
 * El modo (create vs edit) lo determina la presencia de `defaultValues.id`.
 * El submit envía un objeto plano con keys (`category`, `priority`) y
 * el padre se encarga de llamar createTask o updateTask.
 */
export default function TaskFormDialog({
  open,
  onClose,
  onSubmit,
  defaultValues,
  employees = [],
  locations = [],
  currentUser,
  submitError,
}) {
  const isEdit = Boolean(defaultValues?.id);
  const currentEmployeeId = currentUser?.employeeId || currentUser?.employee;
  const currentEmployee = employees.find((e) => e.employeeId === currentEmployeeId) || null;

  const initialAssignee =
    defaultValues?.assignee
      ?? employees.find((e) => e.employeeId === defaultValues?.assigneeId)
      ?? null;

  // Pre-cargar el patient como el shape que espera PatientAutocomplete
  // ({ memberId, memberFullName }). En edición viene como cipId + patientName planos.
  const initialPatient = defaultValues?.cipId
    ? { memberId: defaultValues.cipId, memberFullName: defaultValues.patientName || "" }
    : null;

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      title: defaultValues?.title || "",
      description: defaultValues?.description || "",
      category: defaultValues?.category || "other",
      priority: defaultValues?.priority || "medium",
      dueDate: defaultValues?.dueDate || "",
      patient: initialPatient,
      locationId: defaultValues?.locationId || "",
      relatedType: defaultValues?.relatedType || "",
      assignee: initialAssignee || (isEdit ? null : currentEmployee),
      assignToMyself: !isEdit && !initialAssignee,
    },
  });

  // Reset cuando cambia el defaultValues (al abrir con otra tarea)
  React.useEffect(() => {
    if (open) {
      reset({
        title: defaultValues?.title || "",
        description: defaultValues?.description || "",
        category: defaultValues?.category || "other",
        priority: defaultValues?.priority || "medium",
        dueDate: defaultValues?.dueDate || "",
        patient: defaultValues?.cipId
          ? { memberId: defaultValues.cipId, memberFullName: defaultValues.patientName || "" }
          : null,
        locationId: defaultValues?.locationId || "",
        relatedType: defaultValues?.relatedType || "",
        assignee: initialAssignee || (isEdit ? null : currentEmployee),
        assignToMyself: !isEdit && !initialAssignee,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultValues?.id]);

  const assignToMyself = watch("assignToMyself");

  const onFormSubmit = (data) => {
    const assignee = data.assignToMyself ? currentEmployee : data.assignee;
    onSubmit({
      id: defaultValues?.id || null,
      title: data.title.trim(),
      description: data.description?.trim() || null,
      category: data.category,
      priority: data.priority,
      dueDate: data.dueDate || null,
      cipId: data.patient?.memberId ? String(data.patient.memberId) : null,
      patientName: data.patient?.memberFullName || null,
      locationId: data.locationId || null,
      relatedType: data.relatedType || null,
      assigneeId: assignee?.employeeId || null,
      assigneeName: assignee
        ? `${assignee.firstName} ${assignee.lastName}`
        : null,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
          color: "white",
          py: 2,
          px: 3,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={700}>
            {isEdit ? "Edit Task" : "New Task"}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <form onSubmit={handleSubmit(onFormSubmit)}>
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <Stack spacing={2}>
            {/* Title */}
            <Controller
              name="title"
              control={control}
              rules={{
                required: "Title is required",
                maxLength: { value: 500, message: "Max 500 characters" },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Title"
                  size="small"
                  required
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  fullWidth
                />
              )}
            />

            {/* Description */}
            <Controller
              name="description"
              control={control}
              rules={{ maxLength: { value: 2000, message: "Max 2000 characters" } }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  size="small"
                  multiline
                  rows={3}
                  fullWidth
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              )}
            />

            {/* Category + Priority */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select {...field} label="Category">
                      {TASK_CATEGORIES.map((c) => (
                        <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select {...field} label="Priority">
                      {TASK_PRIORITIES.map((p) => (
                        <MenuItem key={p.key} value={p.key}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color }} />
                            <span>{p.label}</span>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Stack>

            {/* Due date */}
            <Controller
              name="dueDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Due Date"
                  type="date"
                  size="small"
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
            />

            {/* Patient autocomplete (busca por nombre, autocompleta CIP) */}
            <Controller
              name="patient"
              control={control}
              render={({ field }) => (
                <PatientAutocomplete
                  label="Patient"
                  size="small"
                  value={field.value}
                  onPatientSelect={(p) => field.onChange(p)}
                  placeholder="Type at least 3 letters of the patient name..."
                />
              )}
            />

            {/* Location + Related type */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Controller
                name="locationId"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth>
                    <InputLabel>Location</InputLabel>
                    <Select {...field} label="Location">
                      <MenuItem value="">No location</MenuItem>
                      {locations.map((l) => (
                        <MenuItem key={l.locationId} value={l.locationId}>
                          {l.medicalCenter || l.name || `Location ${l.locationId}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
              <Controller
                name="relatedType"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth>
                    <InputLabel>Related to</InputLabel>
                    <Select {...field} label="Related to">
                      <MenuItem value="">None</MenuItem>
                      {RELATED_TYPES.map((r) => (
                        <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Stack>

            {/* Assignee */}
            {employees?.length > 0 && (
              <Stack spacing={0.5}>
                <Controller
                  name="assignToMyself"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                            if (e.target.checked) setValue("assignee", currentEmployee);
                          }}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Assign to myself</Typography>}
                    />
                  )}
                />
                {!assignToMyself && (
                  <Controller
                    name="assignee"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        options={employees}
                        getOptionLabel={(opt) => (opt ? `${opt.firstName} ${opt.lastName}` : "")}
                        isOptionEqualToValue={(opt, val) => opt?.employeeId === val?.employeeId}
                        filterOptions={(options, { inputValue: q }) => {
                          if (!q) return options;
                          const lower = q.toLowerCase();
                          return options.filter(
                            (e) =>
                              e.firstName?.toLowerCase().includes(lower) ||
                              e.lastName?.toLowerCase().includes(lower) ||
                              `${e.firstName} ${e.lastName}`.toLowerCase().includes(lower) ||
                              e.jobPositionName?.toLowerCase().includes(lower)
                          );
                        }}
                        value={field.value}
                        onChange={(_, v) => field.onChange(v)}
                        renderOption={(props, opt) => {
                          const { key, ...rest } = props;
                          return (
                            <li key={opt.employeeId} {...rest}>
                              <Stack>
                                <Typography variant="body2" fontWeight={600}>
                                  {opt.firstName} {opt.lastName}
                                </Typography>
                                {opt.jobPositionName && (
                                  <Typography variant="caption" color="text.secondary">
                                    {opt.jobPositionName}
                                  </Typography>
                                )}
                              </Stack>
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Assign to" size="small" placeholder="Type name or position..." />
                        )}
                        size="small"
                      />
                    )}
                  />
                )}
              </Stack>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            {isEdit ? "Save Changes" : "Create Task"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
